import { t } from '@lingui/core/macro';
import { OUTPUT_HEIGHT, OUTPUT_WIDTH } from 'modules/match-video/domain/assembler/constants';
import { ReviewScene } from 'modules/match-video/domain/interfaces';
import {
  blockWidth,
  Box,
  browserSceneEnv,
  coverInk,
  drawRisingFromLine,
  fillBlock,
  fitTeamPair,
  MASK_PAD_EM,
  measureBlock,
  RISE_DISTANCE,
  SceneEnv,
  tracePolygon,
} from 'modules/match-video/domain/scenes/kit';
import { DangerPlateData } from 'modules/match-video/domain/scenes/scene-data';
import { slashDangerPlateState } from './plate-state';
import { PLATE_BOTTOM, PLATE_LEFT, SLASH, SLASH_FONTS } from './style';

// `.b-dplate` and its parts.
const HEIGHT = 110;
const TOP = OUTPUT_HEIGHT - PLATE_BOTTOM - HEIGHT;
const RIGHT_LIMIT = OUTPUT_WIDTH - PLATE_LEFT;
/** `.b-dtag`: padding 0 64 0 36, the right edge slanting to 86 % at the bottom. */
const TAG = { size: 46, min: 32, maxWidth: 600, padLeft: 36, padRight: 64 };
const TAG_SHAPE = [
  [0, 0],
  [1, 0],
  [0.86, 1],
  [0, 1],
] as const;
/** `.b-dbody`: tucked 30 px under the tag, padding 0 80 0 70, cut 6 % / 97 %; «Команда · Команда» on one line. */
const BODY = { overlap: 30, padLeft: 70, padRight: 80 };
const BODY_SHAPE = [
  [0.06, 0],
  [1, 0],
  [0.97, 1],
  [0, 1],
] as const;
const TEXT = { size: 36, min: 22 };

export const SLASH_DANGER_PLATE_FONTS: readonly string[] = [SLASH_FONTS.tag(TAG.size), SLASH_FONTS.danger(TEXT.size)];

/**
 * Set B dangerous-moment plate: the yellow «ОПАСНО!» tag flies in with an overshoot and blinks white three times,
 * the dark body with both teams slides in after it. Exit: the whole plate flies off to the left.
 */
export async function createSportSlashDangerPlate(data: DangerPlateData, env: SceneEnv = browserSceneEnv): Promise<ReviewScene> {
  await env.loadFonts(SLASH_DANGER_PLATE_FONTS);
  const measurer = env.createCanvas(1, 1);
  const m = measurer.ctx;
  const word = measureBlock(m, t`Опасно!`.toLocaleUpperCase(), {
    maxWidth: TAG.maxWidth,
    size: TAG.size,
    min: TAG.min,
    font: SLASH_FONTS.tag,
    lineHeight: HEIGHT / TAG.size,
  });
  const tag: Box = { left: PLATE_LEFT, top: TOP, width: TAG.padLeft + blockWidth(word) + TAG.padRight, height: HEIGHT };
  const bodyLeft = tag.left + tag.width - BODY.overlap;
  const teams = fitTeamPair(m, data.home.name.toLocaleUpperCase(), data.away.name.toLocaleUpperCase(), {
    maxWidth: Math.max(0, RIGHT_LIMIT - bodyLeft - BODY.padLeft - BODY.padRight),
    size: TEXT.size,
    min: TEXT.min,
    font: SLASH_FONTS.danger,
    separator: '·',
    gap: 'space',
  });

  // The text line is the body's 110 px line; its mask inherits the body's font, so the padding is .08 of it.
  const lineBlock = (block: typeof teams.home) => ({
    ...block,
    lineHeight: HEIGHT,
    baselineOffset: block.baselineOffset + (HEIGHT - block.lineHeight) / 2,
  });
  const home = lineBlock(teams.home);
  const separator = lineBlock(teams.separator);
  const away = lineBlock(teams.away);
  const body: Box = { left: bodyLeft, top: TOP, width: BODY.padLeft + teams.width + BODY.padRight, height: HEIGHT };
  const textLeft = bodyLeft + BODY.padLeft;
  const lineBox = { x: textLeft, y: TOP, width: teams.width, height: HEIGHT + MASK_PAD_EM * home.fontSize };
  const mask = coverInk(m, lineBox, [home, separator, away]);
  measurer.release();

  return {
    draw: (ctx, timeSec, windowSec) => {
      const s = slashDangerPlateState(timeSec, windowSec);
      if (s.exit.alpha <= 0) return;
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';
      ctx.globalAlpha = s.exit.alpha;
      ctx.translate(s.exit.dx, 0);

      if (s.body.alpha > 0) {
        ctx.save();
        ctx.globalAlpha *= s.body.alpha;
        ctx.translate(s.body.dx, 0);
        tracePolygon(ctx, body, BODY_SHAPE);
        ctx.fillStyle = SLASH.ink;
        ctx.fill();
        ctx.clip();
        ctx.fillStyle = SLASH.white;
        drawRisingFromLine(
          ctx,
          mask,
          s.textRise,
          (c) => {
            let x = textLeft;
            fillBlock(c, home, x, TOP, 'left');
            x += blockWidth(home) + teams.gap;
            fillBlock(c, separator, x, TOP, 'left');
            x += blockWidth(separator) + teams.gap;
            fillBlock(c, away, x, TOP, 'left');
          },
          RISE_DISTANCE * HEIGHT,
        );
        ctx.restore();
      }

      if (s.tag.alpha > 0) {
        ctx.save();
        ctx.globalAlpha *= s.tag.alpha;
        ctx.translate(s.tag.dx, 0);
        tracePolygon(ctx, tag, TAG_SHAPE);
        ctx.fillStyle = SLASH.yellow;
        ctx.fill();
        if (s.flash > 0) {
          ctx.save();
          ctx.globalAlpha *= s.flash;
          ctx.fillStyle = SLASH.white;
          ctx.fill();
          ctx.restore();
        }
        ctx.fillStyle = SLASH.ink;
        fillBlock(ctx, word, tag.left + TAG.padLeft, TOP, 'left');
        ctx.restore();
      }
    },
    dispose: () => undefined,
  };
}
